import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Label, TextInput, Spinner, Button, Alert } from "flowbite-react";
import { useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo.png";
import logo_2 from "../assets/logo_2.png";
import { Link } from "react-router-dom";
import AuthorizationPage from "./AuthorizationPage";
import Validate from "../components/Validate";
import { AnimatePresence, motion } from "framer-motion";

const SignupPage = () => {
	const { signup } = useAuth();
	const [showValidate, setShowValidate] = useState(false);
	const [submitError, setSubmitError] = useState("");
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const recaptchaRef = useRef(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		watch,
	} = useForm();

	const onSubmit = async (data) => {
		try {
			const modifiedData = {
				...data,
			};

			setLoading(true); // to avoid multiple submission
			// handle captcha
			const captchaToken = recaptchaRef.current.getValue();
			recaptchaRef.current.reset();
			const response = await signup(modifiedData, captchaToken);
			setLoading(false);

			if (response.status == 200) {
				setShowValidate(true);
				setSubmitError("");
				reset();
			}
		} catch (error) {
			if (!error.response) {
				setSubmitError("No response from server");
			} else {
				setSubmitError(error.response.data.error);
			}
			setLoading(false);
		}
	};

	const onError = async () => {
		console.log("Error: While sending email for signing up");
	};

	return (
		<AuthorizationPage>
			<AnimatePresence mode="wait">
				{!showValidate ? (
					<motion.div 
						key="signup-form"
						initial={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="flex flex-col justify-center items-center bg-white w-full max-w-[420px] py-6 px-4 md:px-6 rounded-lg shadow-xl dark:bg-gray-800 dark:text-white border border-gray-200 dark:border-gray-700 mx-4 my-4"
					>
						<div className="flex items-center justify-center mb-10">
							<img src={logo_2} className="h-11 mr-3" alt="Logo" />
							<span className="self-center whitespace-nowrap text-2xl font-semibold">
								<span style={{ color: "#04c389" }}>Foodo</span>
								<span className="dark:text-[#d9d9d9]" style={{ color: "#313131" }}>scope</span>
								<span style={{ color: "#04c389" }}>.</span>
							</span>
						</div>
						<h1 className="mb-4 font-semibold text-xl text-center text-gray-800 dark:text-white">
							Create Your Account
						</h1>
						<div className="w-20 h-1 bg-blue-500 rounded mb-6"></div>

						<form
							className="flex flex-col gap-4 w-full"
							onSubmit={handleSubmit(onSubmit, onError)}>
							
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								{/* First Name */}
								<div>
									<div className="mb-1 block">
										<Label htmlFor="firstName" value="First Name" className="text-sm font-medium" />
									</div>
									<TextInput
										className="dark:text-black"
										id="firstName"
										type="text"
										color={errors.firstName ? "failure" : null}
										placeholder="Enter first name"
										helperText={
											errors.firstName && errors.firstName.type === "required"
												? "First name is required"
												: errors.firstName && errors.firstName.type === "pattern"
												? "Only letters allowed (no spaces or numbers)"
												: null
										}
										required={true}
										{...register("firstName", {
											required: true,
											pattern: /^[A-Za-z]+$/,
											validate: value => !/\s/.test(value) || "No spaces allowed"
										})}
									/>
								</div>

								{/* Last Name */}
								<div>
									<div className="mb-1 block">
										<Label htmlFor="lastName" value="Last Name" className="text-sm font-medium" />
									</div>
									<TextInput
										className="dark:text-black"
										id="lastName"
										type="text"
										color={errors.lastName ? "failure" : null}
										placeholder="Enter last name"
										helperText={
											errors.lastName && errors.lastName.type === "required"
												? "Last name is required"
												: errors.lastName && errors.lastName.type === "pattern"
												? "Only letters allowed (no spaces or numbers)"
												: errors.lastName && errors.lastName.type === "validate"
												? errors.lastName.message
												: null
										}
										required={true}
										{...register("lastName", {
											required: true,
											pattern: /^[A-Za-z]+$/,
											validate: value => !/\s/.test(value) || "No spaces allowed"
										})}
									/>
								</div>
							</div>

							{/* Email */}
							<div>
								<div className="mb-1 block">
									<Label htmlFor="email1" value="Email Address" className="text-sm font-medium" />
								</div>
								<TextInput
									className="dark:text-black"
									id="email1"
									type="email"
									color={errors.email ? "failure" : null}
									placeholder="Enter a valid email address"
									helperText={
										errors.email && errors.email.type === "required"
											? "Email is required"
											: errors.email && errors.email.type === "pattern"
											? "Enter a valid email address"
											: null
									}
									required={true}
									{...register("email", {
										required: true,
										pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
									})}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>

							<div className="flex justify-center scale-90 transform origin-top mt-1">
								<ReCAPTCHA
									ref={recaptchaRef}
									sitekey="6LeP0IkpAAAAAGXRJvBuiv86G5NP_FkstoWWY35C"
								/>
							</div>

							<Button 
								type="submit" 
								disabled={loading} 
								className="mt-1 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
								{loading ? (
									<>
										<Spinner aria-label="Signing up" size="sm" light={true} />
										<span className="pl-3">Creating Account...</span>
									</>
								) : (
									"Create Account"
								)}
							</Button>

							{/* Error handling */}
							{submitError !== "" ? (
								<Alert color="failure" className="mt-1 text-sm">{submitError}</Alert>
							) : null}
							
							<div className="text-center mt-2">
								<Label className="text-sm">
									Already have an account?{" "}
									<Link
										to="/login"
										className="ml-1 text-blue-600 hover:underline dark:text-blue-500 font-medium">
										Log in
									</Link>
								</Label>
							</div>

							<div className="text-center text-xs text-gray-500 dark:text-gray-400">
								By creating an account, you agree to our{" "}
								<a href="#" className="text-blue-600 hover:underline dark:text-blue-500">
									Terms
								</a>{" "}
								and{" "}
								<a href="#" className="text-blue-600 hover:underline dark:text-blue-500">
									Privacy Policy
								</a>
							</div>
						</form>
					</motion.div>
				) : (
					<motion.div
						key="verification-screen"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						className="flex flex-col justify-center items-center w-full max-w-[500px] mx-4 my-4"
					>
						<Validate email={email} />
						<motion.div 
							initial={{ opacity: 0 }}
							animate={{ opacity: 1, transition: { delay: 0.5 } }}
							className="mt-6"
						>
							<Link
								to="/login"
								className="text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors duration-200">
								Return to Login
							</Link>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</AuthorizationPage>
	);
};

export default SignupPage;
